import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { AppStrings } from "../../utils/constants/AppStrings";

import {
  DEFAULT_REPORTING_SUMMARY_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from "./reportingSummaryConstants";
import type { ReportingSummaryRow } from "./reportingSummaryTypes";
import { getAdminNow } from "../../utils/dateService";

/**
 * Ant Design Select may pass a plain number or an option-like object depending on configuration.
 */
export function normalizeAntSelectNumberValue(value: unknown): number {
  if (typeof value === "object" && value !== null && "value" in value) {
    return Number((value as { value: unknown }).value);
  }
  return Number(value);
}

/** Coerces Ant page-size select values to a supported option. */
export function coerceReportingSummaryPageSize(value: unknown): PageSizeOption {
  const n = normalizeAntSelectNumberValue(value);
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n)
    ? (n as PageSizeOption)
    : DEFAULT_REPORTING_SUMMARY_PAGE_SIZE;
}

export function formatReportingTableDate(d: Dayjs): string {
  return d.format("MM/DD/YY");
}

export function buildPlaceholderRows(total: number): ReportingSummaryRow[] {
  const rows: ReportingSummaryRow[] = [];
  for (let i = 0; i < total; i += 1) {
    const seed = i * 7919;
    const pl = ((seed % 2000) - 1000) / 100;
    const margin = ((seed % 500) - 250) / 100;
    rows.push({
      key: String(i),
      date: getAdminNow().subtract(i % 90, "day"),
      acceptedBets: 10 + (seed % 200),
      acceptedTurnover: (10000 + (seed % 500000)) / 100,
      avgStake: (500 + (seed % 6000)) / 100,
      pl,
      margin,
    });
  }
  return rows;
}

export function formatDecimal(value: number, fractionDigits = 2): string {
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function escapeCsvCell(
  value: string | number | null | undefined,
): string {
  const serialized = String(value ?? "");
  const safe = /^[=+\-@\t\r]/.test(serialized)
    ? `'${serialized}`
    : serialized;
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/** Client-side CSV for reporting summary table (no backend export in V1). */
export function buildReportingSummaryCsv(rows: ReportingSummaryRow[]): string {
  const header = [
    AppStrings.DATE,
    AppStrings.ACCEPTED_BETS,
    AppStrings.REPORTING_ACCEPTED_TURNOVER,
    AppStrings.REPORTING_AVG_STAKE,
    AppStrings.REPORTING_PL_COLUMN,
    AppStrings.MARGIN,
  ];
  const lines = [header.map((h) => escapeCsvCell(h)).join(",")];
  for (const r of rows) {
    const dateStr = dayjs(r.date).format("YYYY-MM-DD");
    lines.push(
      [
        escapeCsvCell(dateStr),
        escapeCsvCell(r.acceptedBets),
        escapeCsvCell(r.acceptedTurnover),
        escapeCsvCell(r.avgStake),
        escapeCsvCell(r.pl),
        escapeCsvCell(r.margin),
      ].join(","),
    );
  }
  return lines.join("\n");
}
